import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { Button } from '@/components/common/Button';
import { CookieConsentPreferences } from './CookieConsentPreferences';

// ── Position class map ───────────────────────────────────────────────────────

const POSITION_CLASSES: Record<string, string> = {
  'bottom': 'fixed inset-x-0 bottom-0 z-40 border-t',
  'bottom-left': 'fixed bottom-4 left-4 z-40 max-w-md rounded-lg border shadow-lg',
  'bottom-right': 'fixed bottom-4 right-4 z-40 max-w-md rounded-lg border shadow-lg',
  'center': 'fixed inset-0 z-50 flex items-center justify-center',
};

const POSITION_ANIMATIONS: Record<string, string> = {
  'bottom': 'slideUp var(--duration-normal) var(--ease-expo) both',
  'bottom-left': 'slideUp var(--duration-normal) var(--ease-expo) both',
  'bottom-right': 'slideUp var(--duration-normal) var(--ease-expo) both',
  'center': 'fadeIn var(--duration-normal) var(--ease-expo) both',
};

// ── Default copy ─────────────────────────────────────────────────────────────

const DEFAULT_TITLE = '';
const DEFAULT_DESCRIPTION =
  'We use cookies to keep you logged in and remember your preferences. Some cookies are optional.';
const DEFAULT_ACCEPT_LABEL = 'Accept all';
const DEFAULT_REJECT_LABEL = 'Reject non-essential';
const DEFAULT_CUSTOMIZE_LABEL = 'Customize';

/**
 * GDPR-compliant cookie consent banner.
 *
 * Design contract:
 * - Position and copy driven by remote banner config (falls back to defaults)
 * - Accept and Reject have EQUAL visual prominence (CNIL)
 * - No cookie wall: app always renders regardless of consent state
 * - Keyboard navigable, ARIA labelled
 */
export function CookieConsentBanner() {
  const {
    showBanner,
    acceptAll,
    rejectNonEssential,
    openPreferences,
    preferencesOpen,
    bannerConfig,
  } = useCookieConsent();

  const bannerRef = useRef<HTMLDivElement>(null);

  // Focus the banner on mount so screen readers announce it
  useEffect(() => {
    if (showBanner && bannerRef.current) {
      // Small delay to ensure the animation has started
      const timer = setTimeout(() => {
        bannerRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showBanner]);

  const position = bannerConfig.position ?? 'bottom';
  const positionClass = POSITION_CLASSES[position] ?? POSITION_CLASSES['bottom'];
  const animation = POSITION_ANIMATIONS[position] ?? POSITION_ANIMATIONS['bottom'];
  const isCenter = position === 'center';

  // Resolve custom copy (fall back to defaults)
  const customCopy = bannerConfig.customCopy;
  const title = customCopy?.title || DEFAULT_TITLE;
  const description = customCopy?.description || DEFAULT_DESCRIPTION;
  const acceptLabel = customCopy?.acceptLabel || DEFAULT_ACCEPT_LABEL;
  const rejectLabel = customCopy?.rejectLabel || DEFAULT_REJECT_LABEL;
  const customizeLabel = customCopy?.customizeLabel || DEFAULT_CUSTOMIZE_LABEL;

  // Theme data attribute for CSS theming (light/dark/auto)
  const theme = bannerConfig.theme ?? 'auto';

  const bannerContent = (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Copy */}
      <div className="flex-1 text-sm text-muted-foreground">
        {title && (
          <p className="mb-1 font-medium text-foreground">{title}</p>
        )}
        <p>
          {description}{' '}
          <Link
            to="/content/cookie-policy"
            className="underline hover:text-foreground"
          >
            Cookie Policy
          </Link>
        </p>
      </div>

      {/* Actions — Accept and Reject are visually equal (both outline) per CNIL */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={rejectNonEssential}>
          {rejectLabel}
        </Button>
        {bannerConfig.showCategories !== false && (
          <Button variant="ghost" size="sm" onClick={openPreferences}>
            {customizeLabel}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={acceptAll}>
          {acceptLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {showBanner && (
        <>
          {isCenter ? (
            <div
              className={positionClass}
              style={{ animation }}
            >
              {/* Backdrop overlay */}
              <div className="absolute inset-0 bg-black/20" />
              <div
                ref={bannerRef}
                role="region"
                aria-label="Cookie consent"
                tabIndex={-1}
                data-theme={theme}
                className="relative z-10 mx-4 max-w-lg rounded-lg border bg-card p-4 shadow-lg sm:p-6"
              >
                {bannerContent}
              </div>
            </div>
          ) : (
            <div
              ref={bannerRef}
              role="region"
              aria-label="Cookie consent"
              tabIndex={-1}
              data-theme={theme}
              className={`${positionClass} bg-card p-4 sm:p-6`}
              style={{
                boxShadow: 'var(--shadow-elevated)',
                animation,
              }}
            >
              {bannerContent}
            </div>
          )}

          {/* Inline keyframes for animations */}
          <style>{`
            @keyframes slideUp {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </>
      )}

      {/* Preferences sheet — always rendered so footer "Manage preferences" works */}
      <CookieConsentPreferences open={preferencesOpen} />
    </>
  );
}
